import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

// customer_users and portal_set_access are not in the generated types yet.
const untyped = supabase as unknown as SupabaseClient;

export interface CustomerPortalLink {
  id: string;
  customer_id: string;
  email: string;
  created_at: string;
  last_link_sent_at: string | null;
  last_login_at: string | null;
  revoked_at: string | null;
}

export type PortalAccessState = 'not_invited' | 'invited' | 'active' | 'revoked';

export function portalAccessState(link: CustomerPortalLink | null | undefined): PortalAccessState {
  if (!link) return 'not_invited';
  if (link.revoked_at) return 'revoked';
  if (link.last_login_at) return 'active';
  return 'invited';
}

const accessKey = (customerId: string | null) => ['customer-portal-access', customerId] as const;

/** Newest portal link for the customer (staff-readable via RLS). */
export function useCustomerPortalAccess(customerId: string | null) {
  return useQuery({
    queryKey: accessKey(customerId),
    enabled: !!customerId,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerPortalLink | null> => {
      if (!customerId) return null;
      const { data, error } = await untyped
        .from('customer_users')
        .select('id, customer_id, email, created_at, last_link_sent_at, last_login_at, revoked_at')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as CustomerPortalLink | null) ?? null;
    },
  });
}

const INVITE_ERRORS: Record<string, string> = {
  staff_account: "This email belongs to a staff account, so it can't be used for the customer portal.",
  no_email: 'Add an email address to the customer first.',
  email_failed: 'The invitation email could not be sent. Check the email settings and try again.',
};

/** Reads the error code from a non-2xx edge-function response, if any. */
async function invokeErrorCode(error: unknown): Promise<string | undefined> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (!(ctx instanceof Response)) return undefined;
  try {
    const body = (await ctx.clone().json()) as { error?: unknown; code?: unknown };
    const code = body.error ?? body.code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

export function usePortalInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data, error } = await supabase.functions.invoke('portal-invite', {
        body: { customer_id: customerId },
      });
      if (error) {
        const code = await invokeErrorCode(error);
        throw new Error(INVITE_ERRORS[code ?? ''] ?? 'Could not send the portal invitation.');
      }
      return data as { ok: boolean; linked: boolean };
    },
    onSuccess: (_data, customerId) => {
      void queryClient.invalidateQueries({ queryKey: accessKey(customerId) });
      toast.success('Portal invitation sent');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function usePortalSetAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { linkId: string; customerId: string; revoked: boolean }) => {
      const { error } = await untyped.rpc('portal_set_access', {
        p_link_id: input.linkId,
        p_revoked: input.revoked,
      });
      if (error) throw error;
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: accessKey(input.customerId) });
      toast.success(input.revoked ? 'Portal access revoked' : 'Portal access restored');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not update portal access');
    },
  });
}
